/**
 * EL PENSUM COMUN DE NEXO Y LO QUE CADA AGENTE TIENE DE EL (PENSUM-NEXO-PANEL-T1; Josse 23-oct, SI de Ares #33254).
 *
 * Contrato propuesto por P (#33255 WIP) y aceptado por A (#33263): GET /api/v1/learning/pensum/nexo ->
 *   { comun: { code, version, estado, unidades: [{ codigo, titulo, tipo: base|tema, disparador: { palabras, rutas },
 *     dondeVive }] } | null,
 *     agentes: [{ agente, total, citadas, verificadas, unidades: [{ codigo, estado: no_citada|citada|verificada,
 *     ultimaCita: { bitacoraId, fecha } }] }] }
 *
 * LO QUE NO SE INVENTA: un estado que no se reconoce se dice "sin leer", no "no citada"; sin pensum comun (Atenea no lo
 * ha entregado) la vista dice que esta en construccion, no pinta 0 de 0. El panel LEE: no decide si una unidad se cito.
 *
 * Sin DOM: lo prueba scripts/check-pensum-nexo.mjs.
 *
 * @module application/pensum/pensumNexo
 */

const de = (o, ...ks) => { for (const k of ks) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const texto = (v) => (v === undefined || v === null ? '' : String(v).trim());
const lista = (v) => (Array.isArray(v) ? v : []);
const numero = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const ESTADOS_NEXO = { no_citada: 'No citada', citada: 'Citada', verificada: 'Verificada por Q' };

/** El texto del estado; uno desconocido no se disfraza de "no citada". */
export function textoDeEstadoNexo(estado) {
  return ESTADOS_NEXO[estado] || 'Estado sin leer';
}

function normalizarUnidadComun(u) {
  const disp = de(u, 'disparador', 'trigger') || {};
  return {
    codigo: texto(de(u, 'codigo', 'code', 'id')),
    titulo: texto(de(u, 'titulo', 'title')),
    tipo: texto(de(u, 'tipo', 'type')).toLowerCase() === 'tema' ? 'tema' : 'base',
    palabras: lista(de(disp, 'palabras', 'words')).map(texto).filter(Boolean),
    rutas: lista(de(disp, 'rutas', 'paths')).map(texto).filter(Boolean),
    dondeVive: texto(de(u, 'dondeVive', 'donde_vive'))
  };
}

function normalizarAgente(a, porCodigo) {
  const unidades = lista(de(a, 'unidades', 'units')).map((u) => {
    const cita = de(u, 'ultimaCita', 'ultima_cita') || null;
    const codigo = texto(de(u, 'codigo', 'code'));
    return {
      codigo,
      titulo: porCodigo.get(codigo)?.titulo || codigo,
      estado: texto(de(u, 'estado', 'state')),
      ultimaCita: cita ? { bitacoraId: numero(de(cita, 'bitacoraId', 'bitacora_id')) || null, fecha: texto(de(cita, 'fecha', 'at')).slice(0, 10) } : null
    };
  });
  const total = numero(de(a, 'total')) || unidades.length;
  // Con unidades, el avance se cuenta de ellas (una verificada tambien esta citada): asi no depende de si la API
  // manda "citadas" con o sin las verificadas adentro. Sin unidades, se leen los totales.
  const citadas = unidades.length ? unidades.filter((u) => u.estado === 'citada' || u.estado === 'verificada').length : numero(de(a, 'citadas'));
  const verificadas = unidades.length ? unidades.filter((u) => u.estado === 'verificada').length : numero(de(a, 'verificadas'));
  return { agente: texto(de(a, 'agente', 'agent')), total, citadas, verificadas, unidades };
}

/** La respuesta de la API (camel o snake) en { comun, agentes }; comun null si no hay pensum entregado. */
export function normalizarPensumNexo(data) {
  const c = de(data, 'comun', 'common');
  const unidadesComun = c ? lista(de(c, 'unidades', 'units')).map(normalizarUnidadComun).filter((u) => u.codigo) : [];
  const comun = c && unidadesComun.length
    ? { code: texto(de(c, 'code', 'codigo')), version: texto(de(c, 'version')), estado: texto(de(c, 'estado')), unidades: unidadesComun }
    : null;
  const porCodigo = new Map(unidadesComun.map((u) => [u.codigo, u]));
  const agentes = lista(de(data, 'agentes', 'agents')).map((a) => normalizarAgente(a, porCodigo)).filter((a) => a.agente);
  return { comun, agentes };
}

/**
 * El semaforo de un agente: verde si todas sus unidades estan citadas (o verificadas), amarillo si alguna, gris si
 * ninguna. Sin unidades no hay semaforo (null): no se pinta verde lo que no tiene que cumplir.
 */
export function semaforo(a) {
  if (!a || !a.total) return null;
  if (a.citadas >= a.total) return 'verde';
  if (a.citadas > 0) return 'amarillo';
  return 'gris';
}
